import { getGridStringOperators, GridFilterOperator } from "@mui/x-data-grid-pro";

import prettifyDate from "./prettifyDate";

interface UserObject {
	firstname?: string;
	lastname?: string;
	email?: string;
	name?: string;
}

type UserFilterRow = UserObject & {
	user?: UserObject;
	isDummyRow?: boolean;
};

/** Value passed to quick/column filters for `field: "user"` columns. */
export function getUserColumnFilterValue(value: unknown, row: UserFilterRow): UserObject | "" {
	if (value === "" || row?.isDummyRow) {
		return "";
	}

	const nested =
		value != null && typeof value === "object" && !Array.isArray(value)
			? value as UserObject
			: row.user;

	return {
		firstname: row.firstname ?? nested?.firstname,
		lastname: row.lastname ?? nested?.lastname,
		email: row.email ?? nested?.email,
		name: row.name ?? nested?.name,
	};
}

function formatUserDisplayName(user: UserObject): string {
	const first = user.firstname?.trim() ?? "";
	const last = user.lastname?.trim() ?? "";

	if (first && last) {
		return `${first} ${last}`;
	}

	if (first) {
		return first;
	}

	if (last) {
		return last;
	}

	if (user.name?.trim()) {
		return user.name.trim();
	}

	return user.email?.trim() ?? "";
}

function userSearchTexts(user: UserObject): string[] {
	const first = user.firstname?.trim().toLowerCase() ?? "";
	const last = user.lastname?.trim().toLowerCase() ?? "";
	const email = user.email?.trim().toLowerCase() ?? "";
	const fullName = [first, last].filter(Boolean).join(" ");
	const display = formatUserDisplayName(user).toLowerCase();
	const texts = [display, fullName, first, last, email];

	if (user.name?.trim()) {
		texts.push(user.name.trim().toLowerCase());
	}

	return texts.filter(Boolean);
}

function userMatchesFilter(
	user: UserObject,
	filterValue: string,
	mode: "contains" | "equals" | "startsWith" | "endsWith",
): boolean {
	const query = filterValue.toLowerCase().trim();
	if (!query) {
		return true;
	}

	const display = formatUserDisplayName(user).toLowerCase();
	const email = user.email?.trim().toLowerCase() ?? "";

	if (mode === "equals") {
		return display === query || email === query;
	}

	if (mode === "startsWith") {
		return display.startsWith(query) || email.startsWith(query) || userSearchTexts(user).some((text) => text.startsWith(query));
	}

	if (mode === "endsWith") {
		return display.endsWith(query) || email.endsWith(query) || userSearchTexts(user).some((text) => text.endsWith(query));
	}

	return userSearchTexts(user).some((text) => text.includes(query));
}

export const handleArrayFiltering = (value: string) => {
	return (cellValue: any[]): boolean => {
		let shouldInclude = false;
		if (cellValue.length === 0) {
			shouldInclude = false;
		}
		const simpleArray = cellValue.map((item: UserObject | string) => {
			if (typeof item === "object" && "firstname" in item && "lastname" in item) {
				return item.firstname && item.lastname ? item.firstname + " " + item.lastname : item.email;
			} else if (typeof item === "object" && "name" in item) {
				return item.name;
			} else {
				return item as string;
			}
		});

		for (const item of simpleArray) {
			if (item && item?.toLowerCase().includes(value.toLowerCase())) {
				shouldInclude = true;
			}
		}
		return shouldInclude;
	};
};

export const handleDateFiltering = (value: string) => {
	return (cellValue: string | number | Date | null): boolean => {
		let shouldInclude = false;
		if (cellValue === null) {
			shouldInclude = false;
		} else {
			const cellDate = prettifyDate(cellValue);
			if (cellDate.toLowerCase().includes(value.toLowerCase())) {
				shouldInclude = true;
			}
		}
		return shouldInclude;
	};
};

export const handleStatusQuickFilter = (value: string) => {
	return (cellValue: boolean): boolean => {
		const label = cellValue ? "active" : "inactive";
		return label.includes(value.toLowerCase());
	};
};

export const handleUserFiltering = (value: string) => {
	return (cellValue: UserObject | "" | null): boolean => {
		if (cellValue == null || cellValue === "") {
			return false;
		}

		return userMatchesFilter(cellValue, value, "contains");
	};
};

export const statusOperators: GridFilterOperator[] = [
	{
		label: "is active",
		value: "is active",
		getApplyFilterFn: () => {
			return (value: boolean) => {
				return value;
			};
		}
	},
	{
		label: "is inactive",
		value: "is inactive",
		getApplyFilterFn: () => {
			return (value: boolean) => {
				return !value;
			};
		}
	}
];

export const arrayOperators: GridFilterOperator[] = [
	{
		label: "is empty",
		value: "is empty",
		getApplyFilterFn: () => {
			return (value: any[]) => {
				return value.length === 0;
			};
		}
	},
	{
		label: "is not empty",
		value: "is not empty",
		getApplyFilterFn: () => {
			return (value: any[]) => {
				return value.length > 0;
			};
		}
	}
];

export const identifierColumnOperators: GridFilterOperator[] = getGridStringOperators()
	.map((operator) => {
		if (operator.value === "contains") {
			return {
				...operator,
				getApplyFilterFn: (filterItem: any) => {
					if (!filterItem.value) return null;
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						return userMatchesFilter(value, filterItem.value, "contains");
					};
				}
			};
		} else if (operator.value === "equals") {
			return {
				...operator,
				getApplyFilterFn: (filterItem: any) => {
					if (!filterItem.value) return null;
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						return userMatchesFilter(value, filterItem.value, "equals");
					};
				}
			};
		} else if (operator.value === "startsWith") {
			return {
				...operator,
				getApplyFilterFn: (filterItem: any) => {
					if (!filterItem.value) return null;
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						return userMatchesFilter(value, filterItem.value, "startsWith");
					};
				}
			};
		} else if (operator.value === "endsWith") {
			return {
				...operator,
				getApplyFilterFn: (filterItem: any) => {
					if (!filterItem.value) return null;
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						return userMatchesFilter(value, filterItem.value, "endsWith");
					};
				}
			};
		} else if (operator.value === "isEmpty") {
			return {
				...operator,
				getApplyFilterFn: () => {
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return true;
						return formatUserDisplayName(value) === "" && !value.email?.trim();
					};
				}
			};
		} else if (operator.value === "isNotEmpty") {
			return {
				...operator,
				getApplyFilterFn: () => {
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						return formatUserDisplayName(value) !== "" || Boolean(value.email?.trim());
					};
				}
			};
		} else if (operator.value === "isAnyOf") {
			return {
				...operator,
				getApplyFilterFn: (filterItem: any) => {
					if (!filterItem.value) return null;
					const filterItems = filterItem.value.map((item: string) => item.toLowerCase().trim());
					return (value: UserObject | "" | null) => {
						if (value == null || value === "") return false;
						const searchTexts = userSearchTexts(value);
						return filterItems.some((item: string) => searchTexts.includes(item));
					};
				}
			};
		} else {
			return operator;
		}
	});
